/**
 * Cennik: próg zależy od liczby kont personelu w panelu operatora.
 *
 * Zastępuje trzy plany nazwane (START / PRO / SCALE), w których cena nie zależała
 * od tego, ile ktoś naprawdę zużywa. Progi liczą STANOWISKA — czyli aktywne
 * członkostwa w `memberships`, po jednym na osobę mającą dostęp do panelu.
 * To jednostka, którą platforma i tak zlicza, więc rachunek wynika z danych,
 * a nie z deklaracji.
 *
 * Uwaga na słowo „operator": w b00k znaczy ono FIRMĘ (`{slug}.b00k.online`,
 * `panel operatora`), a nie osobę. Rozliczamy osoby, więc w interfejsie mówimy
 * „pracownicy" / „stanowiska" — użycie „operatorów" w obu znaczeniach naraz
 * w tym samym ekranie byłoby myleniem klienta o jego własny rachunek.
 *
 * ── DLACZEGO CENY NIE SĄ PRZELICZANE KURSEM ─────────────────────────────────
 * Kwoty w każdej walucie są PODANE, nie policzone. Przeliczanie kursem dnia
 * dałoby ceny w rodzaju „11,76 €", które wyglądają jak pomyłka, zmieniają się
 * między wejściem na stronę a płatnością i wymagałyby źródła kursów jako
 * zależności krytycznej. Cena to decyzja handlowa dla danego rynku, więc leży
 * tu jako liczba. Kwoty spoza PLN są PUNKTEM WYJŚCIA opartym na przybliżonych
 * kursach i wymagają zatwierdzenia przed uruchomieniem sprzedaży.
 */
import { CURRENCY_FOR_LANG } from "./lang.js";
export const TIERS = ["SOLO", "TEAM", "GROUP", "ENTERPRISE"];
export const TIER_LIST = [
    {
        id: "SOLO",
        minSeats: 1,
        maxSeats: 2,
        price: { PLN: 50, EUR: 12, USD: 13, CNY: 89, JPY: 1900, KRW: 17000 },
    },
    {
        id: "TEAM",
        minSeats: 3,
        maxSeats: 5,
        price: { PLN: 90, EUR: 21, USD: 23, CNY: 159, JPY: 3400, KRW: 31000 },
        recommended: true,
    },
    {
        id: "GROUP",
        minSeats: 6,
        maxSeats: 10,
        price: { PLN: 150, EUR: 35, USD: 38, CNY: 269, JPY: 5700, KRW: 52000 },
    },
    {
        id: "ENTERPRISE",
        minSeats: 11,
        maxSeats: null,
        price: null,
    },
];
/** Próg właściwy dla tej liczby stanowisk. */
export function tierForSeats(count) {
    const n = Math.max(1, Math.floor(count));
    return (TIER_LIST.find((t) => n >= t.minSeats && (t.maxSeats === null || n <= t.maxSeats)) ??
        TIER_LIST[TIER_LIST.length - 1]);
}
export function tierById(id) {
    return TIER_LIST.find((t) => t.id === id) ?? TIER_LIST[0];
}
/** Ile stanowisk mieści próg; `null` = bez limitu. */
export function seatLimit(tier) {
    return tier.maxSeats;
}
export function currencyFor(lang) {
    return CURRENCY_FOR_LANG[lang];
}
/**
 * Waluty bez części ułamkowej.
 *
 * Sprawdzone na żywym API Stripe: `amount=1` w JPY to „¥1”, a w CNY „¥0.01”.
 * Pomyłka w tę stronę daje kwotę 100× za niską i nie widać jej w kodzie —
 * widać dopiero na wyciągu.
 */
const BEZ_CZESCI_UŁAMKOWEJ = new Set(["JPY", "KRW"]);
/** Kwota w jednostkach podrzędnych waluty — postaci, której żądają bramki płatnicze. */
export function toMinor(amount, currency) {
    return BEZ_CZESCI_UŁAMKOWEJ.has(currency) ? Math.round(amount) : Math.round(amount * 100);
}
/** Odwrotność `toMinor` — do wyświetlenia kwoty, która przyszła z bramki. */
export function fromMinor(minor, currency) {
    return BEZ_CZESCI_UŁAMKOWEJ.has(currency) ? minor : minor / 100;
}
/** Ile miejsc po przecinku ma ta waluta w zapisie ceny. */
const MIEJSCA = { PLN: 0, EUR: 0, USD: 0, CNY: 0, JPY: 0, KRW: 0 };
/**
 * Cena w zapisie właściwym dla języka.
 *
 * `Intl.NumberFormat` sam stawia symbol po właściwej stronie i wybiera separator
 * tysięcy — `1 900 ¥` po japońsku, `17 000 ₩` po koreańsku, `35 €` po niemiecku
 * (po symbolu) i `€35` po angielsku (przed). Ręczne sklejanie łańcucha dałoby
 * w połowie języków zapis, którego nikt tam nie używa.
 */
export function formatPrice(amount, lang) {
    const currency = currencyFor(lang);
    return new Intl.NumberFormat(lang, {
        style: "currency",
        currency,
        minimumFractionDigits: MIEJSCA[currency],
        maximumFractionDigits: MIEJSCA[currency],
    }).format(amount);
}
/** Cena progu w walucie języka; `null` dla progu negocjowanego. */
export function tierPrice(tier, lang) {
    if (!tier.price)
        return null;
    return formatPrice(tier.price[currencyFor(lang)], lang);
}
/** Zakres progu do etykiety, np. `1–2` albo `11+`. */
export function tierRange(tier) {
    if (tier.maxSeats === null)
        return `${tier.minSeats}+`;
    return `${tier.minSeats}–${tier.maxSeats}`;
}
function dodajMiesiac(d, ile, dzienKotwicy) {
    const r = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + ile, 1));
    // Ostatni dzień miesiąca docelowego — kotwica z 31. nie może wypaść na 3 marca.
    const ostatni = new Date(Date.UTC(r.getUTCFullYear(), r.getUTCMonth() + 1, 0)).getUTCDate();
    r.setUTCDate(Math.min(dzienKotwicy, ostatni));
    return r;
}
const DZIEN_MS = 86_400_000;
export function billingPeriod(anchor, now) {
    const dzien = anchor.getUTCDate();
    // Zaczynamy od miesiąca `now` i cofamy się, jeśli rocznica jeszcze nie minęła.
    let start = dodajMiesiac(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)), 0, dzien);
    if (start.getTime() > now.getTime())
        start = dodajMiesiac(start, -1, dzien);
    const end = dodajMiesiac(start, 1, dzien);
    const days = Math.round((end.getTime() - start.getTime()) / DZIEN_MS);
    // Rozpoczęty dzień jest zużyty: kto podnosi próg o 23:00, płaci za ten dzień.
    const remainingDays = Math.max(0, Math.min(days, Math.ceil((end.getTime() - now.getTime()) / DZIEN_MS)));
    return { start, end, days, remainingDays };
}
/**
 * Dopłata za podniesienie progu w trakcie okresu.
 *
 * Liczymy RÓŻNICĘ cen, nie pełną cenę wyższego progu: klient zapłacił już za
 * bieżący okres według progu, który miał. Naliczenie mu pełnej kwoty znaczyłoby
 * podwójne obciążenie za te same dni.
 *
 * Zwraca `null`, gdy próg docelowy nie ma ceny (ENTERPRISE) — takiej dopłaty nie
 * da się policzyć, bo umowa jest indywidualna, i lepiej to powiedzieć wprost niż
 * pokazać liczbę wziętą znikąd.
 */
export function prorate(args) {
    const currency = currencyFor(args.lang);
    if (!args.to.price)
        return null;
    const period = billingPeriod(args.anchor, args.now);
    const zNowego = args.to.price[currency];
    const zObecnego = args.from.price ? args.from.price[currency] : 0;
    const roznica = Math.max(0, zNowego - zObecnego);
    return {
        amount: Math.round((roznica * period.remainingDays) / period.days),
        nextFullAmount: zNowego,
        currency,
        period,
    };
}
//# sourceMappingURL=pricing.js.map